import React, { useState, useEffect } from 'react';
import { Helmet } from 'react-helmet-async';

import { Box, Container, Typography, Grid, CircularProgress, Modal, Alert, AlertTitle, Button } from '@mui/material';
import EventNoteIcon from '@mui/icons-material/EventNote';
import toast from 'react-hot-toast';
import { useQueryClient } from 'react-query';
import { useNavigate, useOutletContext } from 'react-router-dom';
import { EventsCard } from '../components/card';
import EmptyState from '../components/empty-state';

import { getUserDetails, meetingIsNotInPast, isTenMinutesRemainingOrTimePassed } from '../utils';
import { VideoCall } from '../components/video-call';
import {
  useQueryCheckinStatus,
  useMutationCheckOutUser,
} from '../hooks/react-query/checkinCheckout';
import { useMutateSubmitFirstSession, useQueryGetSurveyStatus } from '../hooks/react-query/useData';

import { useQueryGetMeetingCredentialsToJoin, useMutationCancelAMeeting } from '../hooks/react-query/meetings';
import {
  FirstSessionLog,
  WelcomeSessionSurvey,
  SessionSurveyA,
  SessionSurveyB,
  SessionSurveyC,
  FinalSessionSurveys,
} from '../components/surveys';

const UpcomingEvents = () => {
  const queryClient = useQueryClient();
  const userDetails = getUserDetails();
  const navigate = useNavigate();
  const [invitePayload, setInvitePayload] = useState('');
  const [cancelId, setCancelId] = useState('');

  const [meetingCount, setMeetingCount] = useState(-1);

  const [apiPayload, setPayload] = useState({});

  // State to control survey modal visibility
  const [showSurveyModal, setShowSurveyModal] = useState(false);

  const currentUserRole = userDetails?.role.toLowerCase();
  const upcomingMeetings = useOutletContext();
  const setShowNav = upcomingMeetings?.[1]; // Get setShowNav from context to control navbar visibility

  const currentTime = new Date();
  const upcomingMeetingsPayload = upcomingMeetings?.[2]?.data?.data;
  const upcomingMeetingsList = Array.isArray(upcomingMeetingsPayload)
    ? upcomingMeetingsPayload
    : upcomingMeetingsPayload?.meetings || [];
  const pendingSurveySubmissions = Array.isArray(upcomingMeetingsPayload?.pendingSurveySubmissions)
    ? upcomingMeetingsPayload.pendingSurveySubmissions
    : [];

  const { mutate: cancelAMeeting, isLoading: isCancellingMeeting } = useMutationCancelAMeeting(
    cancelId,
    toast,
    userDetails?.token,
    navigate,
    queryClient
  );

  const {
    data: meetingCredentials,
    refetch: getMeetingCredentialsToJoin,
    isFetching: isLoadingCredentials,
  } = useQueryGetMeetingCredentialsToJoin(invitePayload._id, userDetails?.token, navigate, toast, setInvitePayload);

  const { data, refetch: getUserCheckInStatus } = useQueryCheckinStatus(
    userDetails?.token,
    navigate,
    toast,
    currentUserRole,
    invitePayload._id,
    queryClient
  );

  const { mutate: checkoutUser } = useMutationCheckOutUser(
    toast,
    userDetails?.token,
    navigate,
    currentUserRole,
    queryClient,
    meetingCredentials?.data?.data?._id
  );

  const surveyReferenceId =
    meetingCredentials?.data?.data?._id || data?.data?.data?.meetingId || data?.data?.data?._id;
  const checkoutMeetingId =
    meetingCredentials?.data?.data?._id || data?.data?.data?.meetingId || invitePayload?._id;

  const {
    mutate: submitSurvey,
    isLoading: isSurveySubmitting,
    isError,
  } = useMutateSubmitFirstSession(
    apiPayload,
    navigate,
    toast,
    userDetails?.token,
    checkoutUser,
    checkoutMeetingId,
    surveyReferenceId,
    queryClient,
    false
  );

  const handleManualEndCall = () => {
    if (meetingCredentials?.data?.data?._id) {
      checkoutUser(meetingCredentials.data.data._id);
    }
  };

  const { data: surveyStatus, refetch: getSurveyStatus } = useQueryGetSurveyStatus(
    userDetails?.token,
    navigate,
    toast,
    surveyReferenceId
  );
  const canSubmitSurvey = Boolean(data?.data?.data?.canSubmitSurvey);

  // Handle modal close - clear meeting credentials and close modal
  const handleCloseSurveyModal = () => {
    setShowSurveyModal(false);
    setInvitePayload('');
    // Invalidate and reset meeting credentials query to clear cached data
    queryClient.invalidateQueries(['getMeetingCredentials']);
    queryClient.resetQueries(['getMeetingCredentials']);
    // Refetch upcoming meetings list
    upcomingMeetings[3]();
  };

  const handleCompletePendingSurvey = (pendingItem) => {
    if (!pendingItem?._id) return;
    setInvitePayload({ ...pendingItem, fromPendingSurvey: true });
    setShowSurveyModal(true);
  };

  useEffect(() => {
    upcomingMeetings[3]();
  }, []);

  useEffect(() => {
    getSurveyStatus();
  }, [surveyReferenceId]);

  useEffect(() => {
    if (Object.keys(invitePayload).length > 0) {
      if (invitePayload?.fromPendingSurvey) {
        getUserCheckInStatus();
      } else {
        getMeetingCredentialsToJoin();
        getUserCheckInStatus();
      }
    }
  }, [invitePayload]);

  useEffect(() => {
    if (cancelId) {
      cancelAMeeting();
    }
  }, [cancelId]);

  useEffect(() => {
    if (upcomingMeetingsList) {
      let counter = 0;

      upcomingMeetingsList.forEach((element) => {
        if (
          meetingIsNotInPast(element, currentTime) &&
          !isTenMinutesRemainingOrTimePassed(element?.scheduleTime, true)
        ) {
          counter += 1;
        }
      });

      setMeetingCount(counter);
    }
  }, [upcomingMeetingsList]);

  // Clear meeting credentials and close modal after survey submission
  useEffect(() => {
    if (surveyStatus?.data?.data?.surveyData && showSurveyModal) {
      // Survey has been submitted, close modal and clear meeting
      handleCloseSurveyModal();
      upcomingMeetings?.[3]?.();
    }
  }, [surveyStatus?.data?.data?.surveyData]);

  // Hide/Show navigation based on video call screen
  useEffect(() => {
    const isVideoCallActive = 
      !isLoadingCredentials &&
      meetingCredentials?.data?.data?._id;
    
    if (isVideoCallActive) {
      // Hide navigation when video call screen is active
      setShowNav?.(true);
    } else {
      // Show navigation when on events list
      setShowNav?.(false);
    }

    // Cleanup: Show navigation when component unmounts
    return () => {
      setShowNav?.(false);
    };
  }, [meetingCredentials?.data?.data?._id, isLoadingCredentials, setShowNav]);

  const showSurveyToUser = () => {
    const allProps = {
      apiPayload,
      setPayload,
      isLoading: isSurveySubmitting,
      submitSurvey,
      participantId: invitePayload?.userId,
    };
    if (currentUserRole === 'teacher') {
      if (data?.data?.data?.lastSession) {
        return <FinalSessionSurveys {...allProps} />;
      }
      if (data?.data?.data?.totalCompletedSessions <= 2 || !data?.data?.data?.totalCompletedSessions) {
        return <WelcomeSessionSurvey {...allProps} />;
      }
      if (data?.data?.data?.totalCompletedSessions % 3 === 0) {
        return <SessionSurveyA {...allProps} />;
      }
      if (data?.data?.data?.totalCompletedSessions % 3 === 1) {
        return <SessionSurveyB {...allProps} />;
      }
      if (data?.data?.data?.totalCompletedSessions % 3 === 2) {
        return <SessionSurveyC {...allProps} />;
      }
    }
    return <FirstSessionLog {...allProps} firstLog={data?.data?.data?.totalCompletedSessions === 0} />;
  };

  // Check if user is in meeting screen
  const isInMeeting = !isLoadingCredentials &&
    meetingCredentials?.data?.data?._id &&
    invitePayload &&
    Object.keys(invitePayload).length > 0;

  if (showSurveyModal && !surveyStatus?.data?.data?.surveyData && !isError) return (
    <>
      {/* Survey form modal */}
      <Modal 
        open={showSurveyModal} 
        onClose={handleCloseSurveyModal}
        sx={{
          '& .MuiBackdrop-root': {
            backgroundColor: 'rgba(0, 0, 0, 0.7)',
            backdropFilter: 'blur(10px)',
          }
        }}
      >
        <Box sx={{ 
          position: 'absolute', 
          top: '50%', 
          left: '50%', 
          transform: 'translate(-50%, -50%)', 
          width: '90%', 
          
          maxHeight: '90vh',
          backgroundColor: 'white', 
          // borderRadius: 2, 
          boxShadow: 24, 
          p: 4, 
          overflowY: 'auto',
          outline: 'none',
        }}>
          {showSurveyToUser()}
        </Box>
      </Modal>
    </>
  );

  // If in meeting, render without Container (full width)
  if (isInMeeting) {
    return (
      <>
        <Helmet>
          <title>Edifying Teachers | Upcoming Events</title>
        </Helmet>
        <VideoCall
          meetingDetails={meetingCredentials?.data?.data}
          userName={userDetails?.fname}
          email={userDetails?.email}
          inviteId={invitePayload._id}
          isSurveySubmitted={surveyStatus?.data?.data?.surveyData}
          surveyDataNotFilled={canSubmitSurvey && !surveyStatus?.data?.data?.surveyData && !isError}
          setShowSurveyModal={setShowSurveyModal}
          onManualEndCall={handleManualEndCall}
        />
      </>
    );
  }

  // If not in meeting, render with Container
  return (
    <Container maxWidth="xl" sx={{ p: '0 !important' }}>
      <Helmet>
        <title>Edifying Teachers | Upcoming Events</title>
      </Helmet>

      {pendingSurveySubmissions.length > 0 && !showSurveyModal && (
        <Alert
          severity="warning"
          sx={{ mb: 3, borderRadius: 2, alignItems: 'center' }}
          action={
            <Button
              variant="contained"
              color="warning"
              onClick={() => handleCompletePendingSurvey(pendingSurveySubmissions[0])}
              sx={{ whiteSpace: 'nowrap' }}
            >
              Complete Survey
            </Button>
          }
        >
          <AlertTitle>Action Required: Survey Pending</AlertTitle>
          You have {pendingSurveySubmissions.length} pending survey submission
          {pendingSurveySubmissions.length > 1 ? 's' : ''}.
          {pendingSurveySubmissions[0]?.fname ? ` Latest with ${pendingSurveySubmissions[0].fname} ${pendingSurveySubmissions[0].lname || ''}.` : ''}
          {' '}Please complete it now.
        </Alert>
      )}

      {/* Only show title when not in a meeting */}
      <Typography variant="h3" sx={{ mb: '20px', color: 'text.titles' }}>
        Upcoming Meetings
      </Typography>

      {(upcomingMeetingsList !== undefined || upcomingMeetings[2]?.data?.message) &&
        (!upcomingMeetingsList || upcomingMeetingsList?.length === 0 || meetingCount === 0) && (
          <Box sx={{ mt: 20 }}>
            <EmptyState
              icon={EventNoteIcon}
              title="No Scheduled Meetings"
              description="You don't have any upcoming meetings scheduled at the moment. When meetings are scheduled, they'll appear here."
              iconClassName="calendar-icon"
              dotsClassName="calendar-dots"
            />
          </Box>
        )}

      {upcomingMeetings[4] && (
        <Box sx={{ textAlign: 'center', height: 'fit-content', my: 'auto' }}>
          <CircularProgress
            sx={{ '&.MuiCircularProgress-root': { height: '40px !important', width: '40px !important' } }}
          />
        </Box>
      )}

      <Grid container sx={{ mt: '30px', rowGap: '30px' }}>
        {upcomingMeetingsList?.map(
          (item, index) =>
            meetingIsNotInPast(item, currentTime) &&
            !isTenMinutesRemainingOrTimePassed(item?.scheduleTime, true) && (
              <Grid key={index} item xs={12} md={6} xl={4}>
                <EventsCard
                  item={item}
                  setInvitePayload={setInvitePayload}
                  isLoadingCredentials={isLoadingCredentials}
                  setCancelId={setCancelId}
                  isCancellingMeeting={isCancellingMeeting}
                  cancelId={cancelId}
                  inviteId={invitePayload}
                />
              </Grid>
            )
        )}
      </Grid>
    </Container>
  );
};

export default UpcomingEvents;
